import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, useApi } from "@/lib/api";

const FIELDS = [["name", "Nome del cliente *"], ["legal_name", "Ragione sociale"], ["vat_number", "Partita IVA / CIF"], ["email", "Email"], ["phone", "Telefono"]];
const EMPTY = { name: "", legal_name: "", vat_number: "", email: "", phone: "", notes: "" };

export default function ClientDialog({ open, onOpenChange, client, onSaved, initialName = "" }) {
  const api = useApi();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(client ? { ...EMPTY, ...client } : { ...EMPTY, name: initialName }); }, [open, client]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] || ""]));
      const { data } = client ? await api.patch(`/clients/${client.id}`, body) : await api.post("/clients", body);
      toast.success(client ? "Cliente aggiornato" : `Cliente «${data.name}» creato`);
      onSaved?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="client-dialog">
        <DialogHeader>
          <DialogTitle>{client ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle>
          <DialogDescription>Ogni sito appartiene a un solo cliente: dati, compilazioni e storico restano separati per cliente e per sito.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {FIELDS.map(([k, label]) => (
            <div key={k} className={k === "name" ? "sm:col-span-2" : ""}>
              <Label htmlFor={`client-${k}`} className="text-xs">{label}</Label>
              <Input id={`client-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1" data-testid={`client-${k}-input`} />
            </div>
          ))}
          <div className="sm:col-span-2">
            <Label htmlFor="client-notes" className="text-xs">Note interne</Label>
            <Textarea id="client-notes" rows={3} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1" data-testid="client-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="client-cancel-btn">Annulla</Button>
          <Button onClick={save} disabled={busy || f.name.trim().length < 2} className="bg-sky-600 hover:bg-sky-700" data-testid="client-save-btn">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{client ? "Salva" : "Crea cliente"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
